import React, { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { MapPin, Navigation, X, RefreshCw, ChevronDown, Users, DollarSign } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import GooglePlacesAutocomplete from '../../components/common/GooglePlacesAutocomplete';
import { getConfig } from '../../config';
import { btn } from '../../utils/buttonStyles';
import { roleLabel } from '../../squads/utils/squadRoles';

// Who a class invites when nobody says otherwise; the API uses the same rule.
const CLASS_DEFAULT_ROLES = ['player', 'coach'];

const DAYS = [
  { key: 'SUN', label: 'S' }, { key: 'MON', label: 'M' }, { key: 'TUE', label: 'T' },
  { key: 'WED', label: 'W' }, { key: 'THU', label: 'T' }, { key: 'FRI', label: 'F' },
  { key: 'SAT', label: 'S' }
];

// One definition, used by the initial state and every reset. Previously the
// same object literal appeared three times, so a field added to one of them was
// silently absent after a reset — which is exactly how courtIds came back
// undefined and crashed the modal on reopen.
const emptyForm = () => ({
  subject: '', locationName: '', address: '', placeId: '',
  lat: null, lng: null, city: '', state: '', country: '', zipCode: '',
  date: new Date().toLocaleDateString('en-CA'),
  startTime: '09:00', endTime: '10:00',
  capacity: '', courtIds: [],
});

const emptyRecurrence = () => ({
  enabled: false, frequency: 'weekly', interval: 1,
  daysOfWeek: [], endType: 'count', endCount: 12, endDate: '',
});

const AddEventModal = ({
  isOpen, onClose, onSave, initialData = null, ownerType = null, ownerId = null, academyId = null,
  // A squad's own event asks who it is for: everybody in a normal squad, the
  // players and coaches of a class. Anybody can be ticked or unticked.
  squadMembers = null, isClassSquad = false,
}) => {
  // Capacity, courts and pricing describe a bookable session. They exist only
  // for an offering's sessions — a squad event has no academy behind it, so the
  // fields are hidden rather than shown and then refused by the server.
  const isSession = ownerType === 'program';
  // A court's own events — its open hours, its closures — are at the academy by
  // definition: the court IS part of the venue. Spots and courts still do not
  // apply to it, but the address does.
  const isCourt   = ownerType === 'court';
  const atAcademy = isSession || isCourt;
  const { theme, currentTheme } = useTheme();
  const colorScheme = currentTheme === 'light' ? 'light' : 'dark';

  const [formData, setFormData] = useState(emptyForm);
  const [courts, setCourts] = useState([]);

  // Who's invited — a squad's own new events only.
  const inviteable = (Array.isArray(squadMembers) ? squadMembers : [])
    .filter(m => m.userId && m.status === 'active')
    .map(m => ({ userId: String(m.userId), name: m.username, role: m.role }));
  const showInvitees = ownerType === 'group' && !initialData && inviteable.length > 0;
  const [invitees, setInvitees] = useState(() => new Set());
  useEffect(() => {
    if (!isOpen || !showInvitees) return;
    setInvitees(new Set(inviteable
      .filter(m => !isClassSquad || CLASS_DEFAULT_ROLES.includes(m.role))
      .map(m => m.userId)));
    // Worked out afresh each time the dialog opens, from who is in the squad now.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, showInvitees, isClassSquad]);
  const allInvited = inviteable.length > 0 && inviteable.every(m => invitees.has(m.userId));
  const toggleInvitee = (userId) => setInvitees((prev) => {
    const next = new Set(prev);
    if (next.has(userId)) next.delete(userId);
    else next.add(userId);
    return next;
  });
  const toggleAllInvitees = () => setInvitees(allInvited ? new Set() : new Set(inviteable.map(m => m.userId)));

  useEffect(() => {
    if (!isOpen || !atAcademy || !academyId) return;
    import('../../services/api').then(({ academyAPI }) => {
      // The court picker is for a session choosing which courts it uses. A
      // court's own event has exactly one court — itself.
      if (isSession) {
        academyAPI.courts.list(academyId)
          .then(res => setCourts(res?.courts || res || []))
          .catch(() => setCourts([]));
      }

      // It happens at the academy unless told otherwise, so the venue is
      // prefilled from it rather than made the organiser's problem every time.
      // Only filled when blank, so it never overwrites something already typed,
      // and it stays editable for the night played somewhere else.
      academyAPI.getById(academyId)
        .then((academy) => {
          if (!academy) return;
          setFormData((f) => (f.address || f.locationName ? f : {
            ...f,
            locationName: academy.name || '',
            address: academy.address || '',
          }));
        })
        .catch(() => {});
    });
  }, [isOpen, atAcademy, isSession, academyId]);

  const [recurrence, setRecurrence] = useState({
    enabled: false,
    frequency: 'weekly',
    interval: 1,
    daysOfWeek: [],
    endType: 'count',
    endCount: 12,
    endDate: ''
  });

  const [saving, setSaving] = useState(false);

  // Whether the user has picked days themselves. Until they have, the
  // selection follows the event date; once they have, it is left alone.
  const daysTouched = useRef(false);

  useEffect(() => {
    // A fresh open starts over, so the day selection follows the date again.
    if (isOpen) daysTouched.current = false;
    if (isOpen && initialData) {
      setFormData({
        ...emptyForm(),
        subject: initialData.subject || '',
        locationName: initialData.locationName || '',
        address: initialData.address || '',
        placeId: initialData.placeId || '',
        date: initialData.date || new Date().toLocaleDateString('en-CA'),
        startTime: initialData.startTime || '09:00',
        endTime: initialData.endTime || '10:00',
      });
      setRecurrence(emptyRecurrence());
    } else if (isOpen && !initialData) {
      setFormData(emptyForm());
      setRecurrence(emptyRecurrence());
    }
  }, [isOpen, initialData]);

  const handleInputChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));
  const handleRecurrenceChange = (field, value) => setRecurrence(prev => ({ ...prev, [field]: value }));

  const toggleDay = (day) => {
    daysTouched.current = true;
    setRecurrence(prev => ({
      ...prev,
      daysOfWeek: prev.daysOfWeek.includes(day)
        ? prev.daysOfWeek.filter(d => d !== day)
        : [...prev.daysOfWeek, day]
    }));
  };

  /**
   * Preselect the weekday the event falls on.
   *
   * Sending no days let the server fall back to the start date's weekday, so
   * a repeat with nothing highlighted still produced an event every Tuesday —
   * the form showed one thing and the schedule did another. Seeding the day
   * makes the selection say what will actually happen, and leaves the user
   * free to change it.
   */
  useEffect(() => {
    if (!recurrence.enabled || recurrence.frequency !== 'weekly') return;
    if (daysTouched.current || !formData.date) return;
    const weekday = DAYS[new Date(`${formData.date}T00:00:00`).getDay()].key;
    setRecurrence(prev => (
      prev.daysOfWeek.length === 1 && prev.daysOfWeek[0] === weekday
        ? prev
        : { ...prev, daysOfWeek: [weekday] }
    ));
  }, [recurrence.enabled, recurrence.frequency, formData.date]);

  const handleAddressSelect = (addressData) => {
    setFormData(prev => ({
      ...prev,
      locationName: addressData.placeName || addressData.landmark || prev.locationName || '',
      address:      addressData.fullAddress || '',
      placeId:      addressData.placeId || '',
      lat:          addressData.coordinates?.lat || null,
      lng:          addressData.coordinates?.lng || null,
      city:         addressData.city || '',
      state:        addressData.state || '',
      country:      addressData.country || '',
      zipCode:      addressData.zipCode || ''
    }));
  };

  const handleClearAddress = () => {
    setFormData(prev => ({
      ...prev, locationName: '', address: '', placeId: '',
      lat: null, lng: null, city: '', state: '', country: '', zipCode: ''
    }));
  };

  // Estimate how many occurrences will be generated (for display only)
  const estimateCount = () => {
    if (!recurrence.enabled) return 1;
    if (recurrence.endType === 'count') return Math.min(recurrence.endCount || 1, 30);
    return '≤30';
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      if (!formData.subject.trim()) { toast.error('Please enter an event subject.'); return; }
      if (!formData.address.trim()) {
        toast.error(isSession
          ? 'Add a location for this session.'
          : 'Please select an address from the dropdown after typing.');
        return;
      }
      if (formData.startTime >= formData.endTime) { toast.error('End time must be after start time'); return; }
      const eventDateTime = new Date(`${formData.date}T${formData.startTime}:00`);
      const nowET = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/New_York' }));
      if (eventDateTime < nowET) { toast.error('Cannot create events in the past.'); return; }
      if (recurrence.enabled && recurrence.frequency === 'weekly' && recurrence.daysOfWeek.length === 0) {
        toast.error('Pick at least one day for a weekly repeat.');
        return;
      }

      const payload = {
        subject: formData.subject, locationName: formData.locationName,
        address: formData.address, placeId: formData.placeId,
        lat: formData.lat, lng: formData.lng, city: formData.city,
        state: formData.state, country: formData.country, zipCode: formData.zipCode,
        date: formData.date, startTime: formData.startTime, endTime: formData.endTime,
        ...(isSession ? {
          capacity: formData.capacity ? parseInt(formData.capacity, 10) : null,
          courtIds: formData.courtIds,
        } : {}),
        ...(showInvitees ? { invitees: [...invitees] } : {}),
      };

      if (recurrence.enabled) {
        payload.recurrence = {
          frequency: recurrence.frequency,
          interval: parseInt(recurrence.interval) || 1,
          daysOfWeek: recurrence.frequency === 'weekly' && recurrence.daysOfWeek.length > 0
            ? recurrence.daysOfWeek : undefined,
          endType: recurrence.endType,
          endCount: recurrence.endType === 'count' ? Math.min(parseInt(recurrence.endCount) || 12, 30) : undefined,
          // The picker yields a bare "YYYY-MM-DD", which the API rejects — it
          // wants a date-time. Sent as the last instant of that day, in local
          // time, because the server compares it against occurrences that
          // carry a time of day: midnight would drop an event scheduled on
          // the very date the user chose as the end.
          endDate: recurrence.endType === 'date' && recurrence.endDate
            ? new Date(`${recurrence.endDate}T23:59:59`).toISOString()
            : undefined
        };
      }

      await onSave(payload);

      setFormData(emptyForm());
      setRecurrence(emptyRecurrence());
      onClose();
    } catch (error) {
      console.error('Error saving event:', error);
      toast.error(error.message || 'Failed to save event');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className={`${theme.card} rounded-2xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto`}>
        <h3 className={`text-xl font-semibold ${theme.text} mb-4`}>
          {initialData ? 'Edit Event' : 'Add New Event'}
        </h3>

        <div className="space-y-4">
          {/* Subject */}
          <div>
            <label className={`block text-sm font-medium ${theme.text} mb-2`}>Event Subject *</label>
            <input type="text" value={formData.subject}
              onChange={(e) => handleInputChange('subject', e.target.value)}
              className={`w-full px-4 py-2 rounded-lg ${theme.border} border ${theme.card} ${theme.text}`}
              placeholder="e.g., Team Practice" />
          </div>

          {/* Location */}
          <div>
            <label className={`block text-sm font-medium ${theme.text} mb-2`}>Location *</label>
            <div className="space-y-2">
              <input type="text" value={formData.locationName}
                onChange={(e) => handleInputChange('locationName', e.target.value)}
                placeholder="Venue name"
                className={`w-full px-3 py-2 rounded-lg border ${theme.border} ${theme.card} ${theme.text}`} />
              <GooglePlacesAutocomplete
                googleApiKey={getConfig('GOOGLE_PLACES_API_KEY')}
                placeholder="Search for address..."
                theme={theme} initialValue={formData.address}
                onAddressSelect={handleAddressSelect} />
              {formData.address && (
                <div className={`flex items-center justify-between px-3 py-2 rounded-lg ${theme.backgroundSecondary} border ${theme.border}`}>
                  <div className="flex items-center gap-x-2 min-w-0">
                    <MapPin className="w-4 h-4 text-green-500 flex-shrink-0" />
                    <div className="min-w-0">
                      {formData.locationName && <p className={`text-xs font-medium ${theme.text} truncate`}>{formData.locationName}</p>}
                      <p className={`text-xs ${theme.textMuted} truncate`}>{formData.address}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-x-2 flex-shrink-0 ml-2">
                    <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(formData.address)}${formData.placeId ? `&query_place_id=${formData.placeId}` : ''}`}
                      target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-x-1 text-xs text-blue-500 hover:text-blue-600">
                      <Navigation className="w-3 h-3" /><span>Navigate</span>
                    </a>
                    <button type="button" onClick={handleClearAddress} className={`text-xs ${theme.textMuted} hover:text-red-500`}>
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Date */}
          <div>
            <label className={`block text-sm font-medium ${theme.text} mb-2`}>Date *</label>
            <input type="date" value={formData.date} min={new Date().toLocaleDateString('en-CA')}
              onChange={(e) => handleInputChange('date', e.target.value)}
              className={`w-full px-4 py-2 rounded-lg ${theme.border} border ${theme.card} ${theme.text}`}
              style={{ colorScheme }} />
          </div>

          {/* Time */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={`block text-sm font-medium ${theme.text} mb-2`}>Start Time *</label>
              <input type="time" value={formData.startTime}
                onChange={(e) => handleInputChange('startTime', e.target.value)}
                className={`w-full px-4 py-2 rounded-lg ${theme.border} border ${theme.card} ${theme.text}`}
                style={{ colorScheme }} />
            </div>
            <div>
              <label className={`block text-sm font-medium ${theme.text} mb-2`}>End Time *</label>
              <input type="time" value={formData.endTime}
                onChange={(e) => handleInputChange('endTime', e.target.value)}
                className={`w-full px-4 py-2 rounded-lg ${theme.border} border ${theme.card} ${theme.text}`}
                style={{ colorScheme }} />
            </div>
          </div>

          {/* ── Session details: capacity, courts, price ── */}
          {isSession && (
            <div className={`p-3 rounded-lg border ${theme.border} space-y-3`}>
              <div>
                <label className={`block text-sm font-medium ${theme.text} mb-1.5`}>
                  <Users className="w-4 h-4 inline mr-1" />
                  Spots <span className={`font-normal ${theme.textSecondary}`}>(optional)</span>
                </label>
                <input
                  type="number" min="1" value={formData.capacity}
                  onChange={(e) => setFormData(f => ({ ...f, capacity: e.target.value }))}
                  placeholder="Leave blank for unlimited"
                  className={`w-full px-3 py-2 border rounded-lg ${theme.input}`}
                />
                <p className={`text-xs ${theme.textSecondary} mt-1`}>Per session, not for the whole offering.</p>
              </div>

              {courts.length > 0 && (
                <div>
                  <label className={`block text-sm font-medium ${theme.text} mb-1.5`}>
                    <MapPin className="w-4 h-4 inline mr-1" />
                    Courts <span className={`font-normal ${theme.textSecondary}`}>(optional)</span>
                  </label>
                  <div className="flex flex-wrap gap-1.5">
                    {courts.map(c => {
                      const on = formData.courtIds.includes(c.id);
                      return (
                        <button key={c.id} type="button"
                          onClick={() => setFormData(f => ({
                            ...f,
                            courtIds: on ? f.courtIds.filter(id => id !== c.id) : [...f.courtIds, c.id],
                          }))}
                          className={`px-2.5 py-1 rounded-md text-xs font-medium border transition-colors ${
                            on ? 'bg-blue-600 text-white border-transparent' : `${theme.border} ${theme.textSecondary}`}`}>
                          {c.name}
                        </button>
                      );
                    })}
                  </div>
                  <p className={`text-xs ${theme.textSecondary} mt-1`}>
                    Picking courts also blocks them from other bookings.
                  </p>
                </div>
              )}

              {/* Pricing is attached after the session exists — a price points at
                  an event or its series, and neither has an id until saved. */}
              <button
                type="button"
                disabled
                title="Save the session first, then attach a price to it or its series"
                className={`w-full py-2 rounded-lg text-sm font-medium border border-dashed ${theme.border} ${theme.textSecondary} opacity-60 cursor-not-allowed`}
              >
                <DollarSign className="w-4 h-4 inline mr-1" />
                Attach Price
              </button>
            </div>
          )}

          {/* ── Who's invited — a squad's own events ── */}
          {showInvitees && (
            <div className={`rounded-xl border ${theme.border} p-3`}>
              <div className="flex items-center justify-between mb-2">
                <span className={`text-sm font-medium ${theme.text} flex items-center gap-x-1`}>
                  <Users className="w-4 h-4" /> Who's invited ({invitees.size})
                </span>
                <button type="button" onClick={toggleAllInvitees} className="text-xs text-blue-600 hover:text-blue-700 font-medium">
                  {allInvited ? 'Clear' : 'Everyone'}
                </button>
              </div>
              <div className="max-h-40 overflow-y-auto space-y-1">
                {inviteable.map(m => (
                  <label key={m.userId} className={`flex items-center gap-x-2 text-sm ${theme.text} cursor-pointer`}>
                    <input
                      type="checkbox"
                      checked={invitees.has(m.userId)}
                      onChange={() => toggleInvitee(m.userId)}
                      aria-label={`Invite ${m.name}`}
                    />
                    <span className="flex-1">{m.name}</span>
                    <span className={`text-xs ${theme.textMuted}`}>{roleLabel(m.role)}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* ── Repeat Section ── */}
          {!initialData && (
            <div className={`rounded-xl border ${theme.border} overflow-hidden`}>
              {/* Toggle header */}
              <button type="button"
                onClick={() => handleRecurrenceChange('enabled', !recurrence.enabled)}
                className={`w-full flex items-center justify-between px-4 py-3 ${theme.backgroundSecondary} hover:opacity-90 transition-opacity`}>
                <div className="flex items-center gap-2">
                  <RefreshCw className={`w-4 h-4 ${recurrence.enabled ? 'text-blue-500' : theme.textSecondary}`} />
                  <span className={`text-sm font-medium ${theme.text}`}>
                    Repeat event
                    {recurrence.enabled && (
                      <span className="ml-2 text-xs text-blue-500 font-normal">
                        {estimateCount()} event{estimateCount() !== 1 ? 's' : ''} will be created
                      </span>
                    )}
                  </span>
                </div>
                <div className={`w-10 h-5 rounded-full transition-colors ${recurrence.enabled ? 'bg-blue-500' : 'bg-gray-300 dark:bg-gray-600'}`}>
                  <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${recurrence.enabled ? 'translate-x-5' : 'translate-x-0'}`} />
                </div>
              </button>

              {recurrence.enabled && (
                <div className="px-4 py-3 space-y-3">
                  {/* Frequency */}
                  <div className="flex items-center gap-3">
                    <label className={`text-sm ${theme.textSecondary} w-16 shrink-0`}>Repeat</label>
                    <div className="flex gap-1 flex-1">
                      {['daily', 'weekly', 'monthly'].map(f => (
                        <button key={f} type="button"
                          onClick={() => handleRecurrenceChange('frequency', f)}
                          className={`flex-1 py-1.5 text-xs rounded-lg border transition-colors capitalize
                            ${recurrence.frequency === f
                              ? 'bg-blue-500 border-blue-500 text-white'
                              : `${theme.backgroundSecondary} ${theme.border} ${theme.textSecondary}`}`}>
                          {f}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Interval */}
                  <div className="flex items-center gap-3">
                    <label className={`text-sm ${theme.textSecondary} w-16 shrink-0`}>Every</label>
                    <div className="flex items-center gap-2">
                      <input type="number" min="1" max="12" value={recurrence.interval}
                        onChange={(e) => {
                          const val = e.target.value;
                          handleRecurrenceChange('interval', val === '' ? '' : parseInt(val));
                        }}
                        onBlur={(e) => {
                          const val = parseInt(e.target.value);
                          if (e.target.value === '' || isNaN(val) || val < 1) {
                            handleRecurrenceChange('interval', 1);
                          }
                        }}
                        className={`w-16 px-2 py-1.5 text-sm border ${theme.border} rounded-lg ${theme.card} ${theme.text} text-center`} />
                      <span className={`text-sm ${theme.textSecondary}`}>
                        {recurrence.frequency === 'daily' ? 'day(s)' : recurrence.frequency === 'weekly' ? 'week(s)' : 'month(s)'}
                      </span>
                    </div>
                  </div>

                  {/* Days of week (weekly only) */}
                  {recurrence.frequency === 'weekly' && (
                    <div className="flex items-center gap-3">
                      <label className={`text-sm ${theme.textSecondary} w-16 shrink-0`}>On</label>
                      <div className="flex gap-1">
                        {DAYS.map(({ key, label }) => (
                          <button key={key} type="button"
                            onClick={() => toggleDay(key)}
                            className={`w-8 h-8 rounded-full text-xs font-medium border transition-colors
                              ${recurrence.daysOfWeek.includes(key)
                                ? 'bg-blue-500 border-blue-500 text-white'
                                : `${theme.backgroundSecondary} ${theme.border} ${theme.textSecondary}`}`}>
                            {label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* End condition */}
                  <div className="flex items-center gap-3">
                    <label className={`text-sm ${theme.textSecondary} w-16 shrink-0`}>Ends</label>
                    <div className="flex gap-1 flex-1">
                      {[{ key: 'count', label: 'After' }, { key: 'date', label: 'On date' }].map(({ key, label }) => (
                        <button key={key} type="button"
                          onClick={() => handleRecurrenceChange('endType', key)}
                          className={`flex-1 py-1.5 text-xs rounded-lg border transition-colors
                            ${recurrence.endType === key
                              ? 'bg-blue-500 border-blue-500 text-white'
                              : `${theme.backgroundSecondary} ${theme.border} ${theme.textSecondary}`}`}>
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {recurrence.endType === 'count' && (
                    <div className="flex items-center gap-3 pl-20">
                      <input type="number" min="1" max="30" value={recurrence.endCount}
                        onChange={(e) => {
                          const val = e.target.value;
                          handleRecurrenceChange('endCount', val === '' ? '' : Math.min(parseInt(val), 30));
                        }}
                        onBlur={(e) => {
                          const val = parseInt(e.target.value);
                          if (e.target.value === '' || isNaN(val) || val < 1) {
                            handleRecurrenceChange('endCount', 1);
                          }
                        }}
                        className={`w-16 px-2 py-1.5 text-sm border ${theme.border} rounded-lg ${theme.card} ${theme.text} text-center`} />
                      <span className={`text-sm ${theme.textSecondary}`}>occurrences (max 30)</span>
                    </div>
                  )}

                  {recurrence.endType === 'date' && (
                    <div className="flex items-center gap-3 pl-20">
                      <input type="date" value={recurrence.endDate} min={formData.date || new Date().toLocaleDateString('en-CA')}
                        onChange={(e) => handleRecurrenceChange('endDate', e.target.value)}
                        className={`flex-1 px-2 py-1.5 text-sm border ${theme.border} rounded-lg ${theme.card} ${theme.text}`}
                        style={{ colorScheme }} />
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Buttons */}
          <div className="flex gap-3 mt-6">
            <button onClick={onClose} disabled={saving} className={`flex-1 ${btn.base} ${btn.md} ${theme.buttonGhost}`}>
              Cancel
            </button>
            <button onClick={handleSave} disabled={saving} className={`flex-1 ${btn.base} ${btn.md} ${theme.buttonPrimary}`}>
              {saving ? 'Saving...' : initialData ? 'Update Event' : recurrence.enabled ? `Create ${estimateCount()} Events` : 'Add Event'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddEventModal;
